import { identity } from "./function";

/**
 * A two-way transformation.
 */
export interface Bijection<A, B> {
  read(value: A): B;
  write(value: B): A;
}

export function bijectionCompose<A, B, C>(
  first: Bijection<A, B>,
  second: Bijection<B, C>,
): Bijection<A, C> {
  return {
    read(a) {
      return second.read(first.read(a));
    },
    write(c) {
      return first.write(second.write(c));
    },
  };
}

export function bijectionDefault<A, B>(
  bijection: Bijection<A, B>,
  defaultValue: B,
): Bijection<A | undefined, B> {
  return {
    read(a) {
      return a === undefined ? defaultValue : bijection.read(a);
    },
    write: bijection.write,
  };
}

export function bijectionIdentity<A>(): Bijection<A, A> {
  return { read: identity, write: identity };
}

export function bijectionOptional<A, B>(
  bijection: Bijection<A, B>,
): Bijection<A | undefined, B | undefined> {
  return {
    read(a) {
      return a === undefined ? undefined : bijection.read(a);
    },
    write(b) {
      return b === undefined ? undefined : bijection.write(b);
    },
  };
}

export function bijectionRef<A, B>(
  ref: () => Bijection<A, B>,
): Bijection<A, B> {
  return {
    read(a) {
      return ref().read(a);
    },
    write(b) {
      return ref().write(b);
    },
  };
}

export function bijectionInvert<A, B>(
  bijection: Bijection<A, B>,
): Bijection<B, A> {
  return {
    read(a) {
      return bijection.write(a);
    },
    write(b) {
      return bijection.read(b);
    },
  };
}
